import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';
import { X, CheckCircle, XCircle, ChevronDown, ChevronRight, Save, Shield } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../../../../components/api/apiService';

export interface RoutePrivilege {
    id: string;
    uid: string;
    label: string;
    path: string | null;
    icon: string;
    hasAccess: boolean;
    children?: RoutePrivilege[];
}

interface Props {
    roleName: string;
    roleId: string;
    tenantId: string;
    routes: RoutePrivilege[];
    onClose: () => void;
}

const Toast: React.FC<{ message: string }> = ({ message }) => (
    <div className="absolute top-4 right-6 z-50 bg-green-600 text-white text-sm px-4 py-2 rounded shadow-lg animate-fadeIn">
        ✅ {message}
    </div>
);

const ViewRoutePrivilegesModal: React.FC<Props> = ({
    roleName,
    roleId,
    tenantId,
    routes,
    onClose
}) => {
    const [editedRoutes, setEditedRoutes] = useState<RoutePrivilege[]>(routes);
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const toggleAccess = (target: RoutePrivilege, nodes = editedRoutes): RoutePrivilege[] => {
        return nodes.map((r) => {
            if (r.uid === target.uid) return { ...r, hasAccess: !r.hasAccess };
            if (r.children) return { ...r, children: toggleAccess(target, r.children) };
            return r;
        });
    };

    const handleToggle = (route: RoutePrivilege) => {
        const updated = toggleAccess(route);
        setEditedRoutes(updated);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                tenantId,
                roleId,
                routes: editedRoutes
            };
            await apiService.post('/api/RoutePermission/upsert', payload, 'backend');
            setToastMessage('Permissions saved successfully!');
        } catch (error) {
            console.error('Save failed:', error);
            setToastMessage('❌ Failed to save permissions');
        } finally {
            setSaving(false);
            setTimeout(() => setToastMessage(null), 2000);
        }
    };

    const selectedRoute = findRoute(editedRoutes, selectedRouteId);

    return createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 md:p-6 font-sans">
            <div className="bg-neutral-900 text-white rounded-2xl w-full max-w-5xl shadow-2xl relative max-h-[92vh] overflow-hidden border border-neutral-800 transition-colors duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-neutral-800">
                    <div className="flex-1">
                        <h2 className="text-xl font-bold tracking-tight">
                            Route Privileges for <span className="text-indigo-400">{roleName}</span>
                        </h2>
                        <p className="text-xs text-gray-400 mt-1">Configure feature access and visibility for this role.</p>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/20 active:scale-95 transition-all disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-all"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {toastMessage && <Toast message={toastMessage} />}

                <div className="flex h-full max-h-[calc(92vh-100px)] overflow-hidden">
                    {/* Left Tree */}
                    <div className="w-full md:w-2/3 overflow-y-auto p-6 border-r border-neutral-800 custom-scrollbar">
                        <RouteTree
                            routes={editedRoutes}
                            onToggle={handleToggle}
                            selected={selectedRouteId}
                            onSelect={setSelectedRouteId}
                        />
                    </div>

                    {/* Right Panel */}
                    <div className="hidden md:block w-1/3 p-6 bg-neutral-950/30">
                        {selectedRoute ? (
                            <div className="space-y-4">
                                <h4 className="font-bold text-white border-b border-neutral-800 pb-2">Selected Route Details</h4>
                                <div className="space-y-3">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">UID</span>
                                        <span className="text-sm font-mono bg-neutral-800 px-2 py-1 rounded inline-block w-fit mt-1">{selectedRoute.uid}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Display Label</span>
                                        <span className="text-sm text-white font-medium">{selectedRoute.label}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Nav Path</span>
                                        <span className="text-sm text-gray-500 font-mono italic">{selectedRoute.path || '—'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 pt-2">
                                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Current Access:</span>
                                        {selectedRoute.hasAccess ? (
                                            <span className="bg-green-900/30 text-green-400 px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 border border-green-800/50">
                                                <CheckCircle className="w-3 h-3" /> Granted
                                            </span>
                                        ) : (
                                            <span className="bg-red-900/30 text-red-400 px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 border border-red-800/50">
                                                <XCircle className="w-3 h-3" /> Denied
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                                <Shield className="w-12 h-12 text-neutral-700 mb-2" />
                                <p className="text-gray-500 text-xs">Click a route from the tree to view specific configuration details.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const RouteTree: React.FC<{
    routes: RoutePrivilege[];
    onToggle: (route: RoutePrivilege) => void;
    selected: string | null;
    onSelect: (uid: string) => void;
}> = ({ routes, onToggle, selected, onSelect }) => {
    const [expanded, setExpanded] = useState<{ [uid: string]: boolean }>({});

    return (
        <ul className="ml-1 space-y-1">
            {routes.map((route) => {
                const isExpanded = expanded[route.uid];

                // Dynamically resolve the icon component from Lucide namespace
                const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.SquareDot;

                return (
                    <li key={route.uid}>
                        <div
                            className={clsx(
                                'flex items-center justify-between rounded-xl px-4 py-3 transition-all group border',
                                'cursor-pointer',
                                selected === route.uid
                                    ? 'bg-indigo-900/20 border-indigo-500/50'
                                    : 'hover:bg-neutral-800 border-transparent'
                            )}
                            onClick={() => onSelect(route.uid)}
                        >
                            <div className="flex items-center gap-2">
                                {route.children && route.children.length > 0 ? (
                                    <button
                                        className="text-gray-400 group-hover:text-indigo-500 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded((prev) => ({ ...prev, [route.uid]: !isExpanded }));
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                ) : (
                                    <span className="w-4 h-4" />
                                )}
                                <IconComponent className="w-4 h-4 text-yellow-400" />
                                <span className="text-white text-sm font-medium">{route.label}</span>
                            </div>
                            <button
                                className={clsx(
                                    "flex items-center justify-center w-8 h-8 rounded-full transition-all active:scale-90",
                                    route.hasAccess
                                        ? "bg-green-900/30 text-green-400 hover:bg-green-900/50"
                                        : "bg-red-900/20 text-red-400 hover:bg-red-900/40"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(route);
                                }}
                            >
                                {route.hasAccess ? (
                                    <CheckCircle className="text-green-400 w-4 h-4" />
                                ) : (
                                    <XCircle className="text-red-400 w-4 h-4" />
                                )}
                            </button>
                        </div>
                        {route.children && isExpanded && (
                            <div className="ml-4">
                                <RouteTree
                                    routes={route.children}
                                    onToggle={onToggle}
                                    selected={selected}
                                    onSelect={onSelect}
                                />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

function findRoute(routes: RoutePrivilege[], uid: string | null): RoutePrivilege | undefined {
    if (!uid) return undefined;
    for (const r of routes) {
        if (r.uid === uid) return r;
        if (r.children) {
            const found = findRoute(r.children, uid);
            if (found) return found;
        }
    }
    return undefined;
}

export default ViewRoutePrivilegesModal;
